import { useRef, useState } from 'react'
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
  StripeExpressCheckoutElementOptions,
  StripeError,
} from '@stripe/stripe-js'
import { useTranslation } from 'react-i18next'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { SHIPPING_OPTION_FREE } from '../../stores/shippingStore/shipping.consts'
import { ADDRESS_SOURCE } from '../../stores/addressStore/address.consts'
import { type AddressSource } from '../../stores/addressStore/address.types'
import { shouldCapturePhone } from '../../stores/addressStore/address.utils'
import { useAddressStore } from '../../stores/RootStoreProvider'
import { COUNTRY_CODES, getDefaultCountryId } from '../../config/countries-data'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { logger } from '../../services/logger'
import UserManager from '../../services/UserManager'
import { type CreateOrderParams } from '../../services/OrderManager.types'
import useApi from '../../api/apiProvider'
import { useExpressCheckoutDestinationChecker } from '../../features/cart/hooks/useExpressCheckoutDestinationChecker'
import { useCheckoutFunnelAnalytics } from '../../features/cart/hooks/useCheckoutFunnelAnalytics'
import {
  CHECKOUT_ENTRY_POINT,
  type CheckoutEntryPoint,
} from '../../features/cart/cart.consts'
import { useCheckout } from '../Checkout/CheckoutContext'
import { confirmExpressCheckoutOrder } from '../Checkout/confirmExpressCheckoutOrder'
import { ExternalToPaymentMethod } from '../StripePaymentForm/StripePaymentForm.utils'
import useExpressCheckoutPricingFetcher from '../Checkout/useExpressCheckoutPricingFetcher'
import useExpressCheckoutShippingOptionsFetcher from '../Checkout/useExpressCheckoutShippingOptionsFetcher'
import {
  getExpressCheckoutRateStripeAmount,
  findExpressCheckoutRateById,
  type ExpressCheckoutPricing,
} from '../Checkout/expressCheckoutPricing'
import type { ShippingOption } from '../../stores/shippingStore/shipping.types'
import type { CheckoutSummary } from '../Pricing/types/checkout.types'
import {
  getShippingRateLabel,
  toStripeShippingRate,
  getOfferedRates,
} from './StripeExpressCheckout.utils'
import {
  toStripeSheetRows,
  EXPRESS_CHECKOUT_FORMAT,
} from '../Pricing/stripeSheetRows'
import ExpressCheckoutLoadingButton from './ExpressCheckoutLoadingButton'
import { useExpressCheckoutAvailability } from './ExpressCheckoutAvailabilityProvider'

// Disables go in `paymentMethods`, never the deprecated `wallets`: mixing the two
// makes the runtime fall back to wallets-based logic, which has no Link control.
// The always-latest js.stripe.com/v3 runtime honors it; the pinned types don't
// declare it yet, hence the cast — drop it when the Stripe types are bumped.
const EXPRESS_CHECKOUT_OPTIONS = {
  buttonHeight: 52,
  buttonType: {
    applePay: 'plain',
    googlePay: 'buy',
  },
  paymentMethods: {
    applePay: 'auto',
    googlePay: 'auto',
    link: 'never',
    paypal: 'never',
    amazonPay: 'never',
    klarna: 'never',
  },
} as StripeExpressCheckoutElementOptions

const SUBTOTAL_ROW_KEY = 'order.checkout.payment_summary_rows.subtotal'

interface ExpressCheckoutConfirmBase {
  confirmationTokenId: string
  billingDetails: StripeExpressCheckoutElementConfirmEvent['billingDetails']
  expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType']
}

export type ExpressCheckoutConfirmData = ExpressCheckoutConfirmBase &
  (
    | { addressSource: 'checkout-form' }
    | {
        addressSource: 'express-checkout'
        shippingAddress: NonNullable<
          StripeExpressCheckoutElementConfirmEvent['shippingAddress']
        >
        quote: {
          summary: CheckoutSummary
          shippingOption?: ShippingOption
          shippingOptions: ShippingOption[]
        }
      }
  )

export type ExpressCheckoutConfirmResult =
  | { ok: true }
  | { ok: false; reason: 'fail' | 'invalid_shipping_address' }

interface StripeExpressCheckoutProps {
  addressSource: AddressSource
  shouldBlockOpen: () => boolean
  onOrderSubmitted: () => void
  callCreateOrder: (params: Omit<CreateOrderParams, 'bagAnalytics'>) => void
  isCartFlow: boolean
  entryPoint: CheckoutEntryPoint
}

function StripeExpressCheckout({
  addressSource,
  shouldBlockOpen,
  onOrderSubmitted,
  callCreateOrder,
  isCartFlow,
  entryPoint,
}: StripeExpressCheckoutProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation()
  const checkout = useCheckout()
  const addressStore = useAddressStore()
  const api = useApi()
  const isDestinationSupportedChecker = useExpressCheckoutDestinationChecker()
  const { markWalletUnavailable } = useExpressCheckoutAvailability()
  const { trackCheckoutStarted, trackCompleteOrderTapped } =
    useCheckoutFunnelAnalytics({ isCartFlow })
  const fetchExpressCheckoutPricing = useExpressCheckoutPricingFetcher()
  const fetchExpressCheckoutShippingOptions =
    useExpressCheckoutShippingOptionsFetcher()
  const lastPaymentTypeRef = useRef<
    StripeExpressCheckoutElementClickEvent['expressPaymentType'] | null
  >(null)
  const lastQuoteRef = useRef<{
    pricing: ExpressCheckoutPricing
    shippingOptions: ShippingOption[]
    checkoutId: string
  } | null>(null)
  const [isWalletReady, setIsWalletReady] = useState(false)

  if (!stripe || !elements) {
    return null
  }

  const isExpressCheckoutAddress =
    addressSource === ADDRESS_SOURCE.EXPRESS_CHECKOUT
  const productLabel = t(SUBTOTAL_ROW_KEY)

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    const isBlocked = shouldBlockOpen()
    analytics.track(EVENT_NAMES.EXPRESS_CHECKOUT_PAY_TAPPED, {
      paymentMethod: event.expressPaymentType,
      'Is Blocked': isBlocked,
      'Checkout Entry Point': entryPoint,
    })
    if (isBlocked) {
      // The click event carries no reject(); leaving it unresolved is what
      // keeps the express checkout sheet from opening.
      return
    }
    lastPaymentTypeRef.current = event.expressPaymentType
    event.resolve({
      emailRequired: !UserManager.getUserEmail(),
      phoneNumberRequired:
        isExpressCheckoutAddress &&
        readOutsideReaction(() =>
          shouldCapturePhone({
            countryId: getDefaultCountryId(),
            tiles: checkout.orderTiles,
          })
        ),
      shippingAddressRequired: isExpressCheckoutAddress,
      lineItems: toStripeSheetRows(t, checkout.pricingSummary, {
        productLabel,
        format: EXPRESS_CHECKOUT_FORMAT,
      }),
      ...(isExpressCheckoutAddress
        ? {
            allowedShippingCountries: COUNTRY_CODES,
            shippingRates: [
              {
                id: SHIPPING_OPTION_FREE,
                displayName: getShippingRateLabel(t, SHIPPING_OPTION_FREE),
                amount: 0,
              },
            ],
          }
        : {}),
    })
    if (entryPoint === CHECKOUT_ENTRY_POINT.BAG_EXPRESS_CHECKOUT) {
      trackCheckoutStarted(entryPoint)
    }
  }

  const handleShippingAddressChange = async (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => {
    try {
      const [{ pricing, checkoutId }, delivery, isDestinationSupported] =
        await Promise.all([
          fetchExpressCheckoutPricing(event.address),
          fetchExpressCheckoutShippingOptions(event.address),
          isDestinationSupportedChecker(event.address),
        ])
      if (!isDestinationSupported) {
        logger.warning(
          'Express checkout address cannot receive this order, rejecting',
          {
            country: event.address.country,
          }
        )
        event.reject()
        return
      }
      const offeredRates = getOfferedRates(pricing.rates, delivery)
      const defaultRate = offeredRates[0]
      if (!defaultRate) {
        logger.warning(
          'Express checkout address has no shipping rates, rejecting',
          {
            country: event.address.country,
          }
        )
        event.reject()
        return
      }
      lastQuoteRef.current = {
        pricing,
        shippingOptions: delivery.options,
        checkoutId,
      }
      elements.update({
        amount: getExpressCheckoutRateStripeAmount(defaultRate),
      })
      event.resolve({
        shippingRates: offeredRates.map((rate) =>
          toStripeShippingRate(t, rate, delivery.options)
        ),
        lineItems: toStripeSheetRows(t, defaultRate.summary, {
          productLabel,
          format: EXPRESS_CHECKOUT_FORMAT,
        }),
      })
    } catch (error) {
      logger.error('Express checkout shipping address change failed', error)
      event.reject()
    }
  }

  const handleShippingRateChange = (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => {
    const quote = lastQuoteRef.current
    const rate = quote
      ? findExpressCheckoutRateById(quote.pricing, event.shippingRate.id)
      : undefined
    if (!rate) {
      event.reject()
      return
    }
    elements.update({ amount: getExpressCheckoutRateStripeAmount(rate) })
    event.resolve({
      lineItems: toStripeSheetRows(t, rate.summary, {
        productLabel,
        format: EXPRESS_CHECKOUT_FORMAT,
      }),
    })
  }

  const handleConfirm = async (
    event: StripeExpressCheckoutElementConfirmEvent
  ) => {
    const failPayment = (
      reason: 'fail' | 'invalid_shipping_address',
      error?: unknown
    ) => {
      logger.error('Express checkout confirm failed', error, {
        reason,
        paymentMethod: event.expressPaymentType,
      })
      analytics.track(EVENT_NAMES.EXPRESS_CHECKOUT_PAYMENT_ERROR, {
        paymentMethod: event.expressPaymentType,
        reason,
      })
      event.paymentFailed({ reason })
    }

    try {
      const quote = lastQuoteRef.current
      const selectedRate =
        quote && event.shippingRate
          ? findExpressCheckoutRateById(quote.pricing, event.shippingRate.id)
          : undefined
      if (selectedRate) {
        elements.update({
          amount: getExpressCheckoutRateStripeAmount(selectedRate),
        })
      }
      const submitResult = await elements.submit()
      if (submitResult.error) {
        failPayment('fail')
        return
      }

      const { confirmationToken, error } = await stripe.createConfirmationToken(
        {
          elements,
        }
      )
      if (error || !confirmationToken) {
        failPayment('fail')
        return
      }

      analytics.track(EVENT_NAMES.EXPRESS_CHECKOUT_RECEIVED_PAYMENT_METHOD, {
        paymentMethod: event.expressPaymentType,
      })

      const confirmBase = {
        confirmationTokenId: confirmationToken.id,
        billingDetails: event.billingDetails,
        expressPaymentType: event.expressPaymentType,
      }

      let confirmData: ExpressCheckoutConfirmData
      if (isExpressCheckoutAddress) {
        const expressCheckoutShippingOption = quote?.shippingOptions.find(
          (option) => option.shippingOptionName === event.shippingRate?.id
        )
        if (
          !event.shippingAddress ||
          !quote ||
          !selectedRate ||
          selectedRate.summary.totalPrice <= 0
        ) {
          logger.error(
            'Express checkout confirm has no usable quote for its address',
            null,
            {
              hasShippingAddress: Boolean(event.shippingAddress),
              hasQuote: Boolean(quote),
              totalPrice: selectedRate?.summary.totalPrice,
              shippingRateId: event.shippingRate?.id,
            }
          )
          failPayment('fail')
          return
        }
        confirmData = {
          ...confirmBase,
          addressSource: ADDRESS_SOURCE.EXPRESS_CHECKOUT,
          shippingAddress: event.shippingAddress,
          quote: {
            summary: selectedRate.summary,
            shippingOption: expressCheckoutShippingOption,
            shippingOptions: quote.shippingOptions,
          },
        }
      } else {
        confirmData = {
          ...confirmBase,
          addressSource: ADDRESS_SOURCE.CHECKOUT_FORM,
        }
      }

      trackCompleteOrderTapped(
        ExternalToPaymentMethod[event.expressPaymentType]
      )
      const result = await confirmExpressCheckoutOrder(
        {
          checkout,
          getToken: () => api.getToken(),
          shouldValidateAddressInServer: readOutsideReaction(
            () => addressStore.shouldValidateAddressInServer
          ),
          callCreateOrder,
        },
        confirmData
      )
      if (!result.ok) {
        failPayment(result.reason)
        return
      }
      onOrderSubmitted()
    } catch (error) {
      failPayment('fail', error)
    }
  }

  const handleCancel = () => {
    lastQuoteRef.current = null
    analytics.track(EVENT_NAMES.EXPRESS_CHECKOUT_PAYMENT_ABORTED, {
      paymentMethod: lastPaymentTypeRef.current,
    })
  }

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    if (!event.availablePaymentMethods) {
      markWalletUnavailable()
      return
    }
    setIsWalletReady(true)
    analytics.track(EVENT_NAMES.WALLET_AVAILABLE, {
      'Available Payment Methods': event.availablePaymentMethods,
    })
  }

  const handleLoadError = (event: {
    elementType: 'expressCheckout'
    error: StripeError
  }) => {
    markWalletUnavailable()
    const { error } = event
    logger.error('Failed loading stripe ExpressCheckoutElement', error)
    analytics.track(EVENT_NAMES.WALLET_LOAD_ERROR, {
      errorType: error.type,
      errorCode: error.code,
      errorMessage: error.message,
    })
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: EXPRESS_CHECKOUT_OPTIONS.buttonHeight,
      }}
    >
      <div
        style={{
          visibility: isWalletReady ? 'visible' : 'hidden',
        }}
      >
        <ExpressCheckoutElement
          options={EXPRESS_CHECKOUT_OPTIONS}
          onReady={handleReady}
          onLoadError={handleLoadError}
          onClick={handleClick}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          onShippingAddressChange={handleShippingAddressChange}
          onShippingRateChange={handleShippingRateChange}
        />
      </div>
      {!isWalletReady && (
        <div style={{ position: 'absolute', inset: 0 }}>
          <ExpressCheckoutLoadingButton />
        </div>
      )}
    </div>
  )
}

export default StripeExpressCheckout
